import { useState, useEffect } from "react";
import LogsTable from "../components/LogsTable";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Pagination,
  PaginationItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useMedia } from "../context/MediaContext";
import ClearIcon from "@mui/icons-material/Clear";
import { EmptyState, Error, Loading, PageHeader } from "../components";
import { useTheme } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ViewListRounded from "@mui/icons-material/ViewListRounded";
import DataObjectRounded from "@mui/icons-material/DataObjectRounded";

import {
  getInLogs,
  getOutLogs,
  getReqIdLogs,
  getTransIdLogs,
  fetchProviders,
} from "../utils/api";
import LogsFilters from "../components/LogsFilters";
import { LogsMobileComponent } from "../components";
const LogsPage: React.FC = () => {
  const [logType, setLogType] = useState<"incoming" | "outgoing" | "id">(
    "incoming",
  );

  const handleLogTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newType: "incoming" | "outgoing" | "id" | null,
  ) => {
    if (newType !== null) setLogType(newType);
  };
  const { isTablet, isMobile600 } = useMedia();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    ts_to: string | null;
    ts_from: string | null;
    provider: string | null;
    status: number[];
    url_like: string | null;
  }>({
    ts_from: null,
    ts_to: null,
    provider: null,
    status: [],
    url_like: null,
  });

  const logRequests: {
    incoming: () => Promise<any>;
    outgoing: () => Promise<any>;
    transaction: (id: string) => Promise<any>;
    request: (id: string) => Promise<any>;
  } = {
    incoming: () => getInLogs(page, filters),
    outgoing: () => getOutLogs(page, filters),
    transaction: (id: string) => getTransIdLogs(id, filters),
    request: (id: string) => getReqIdLogs(id, filters),
  };
  const [idType, setIdType] = useState("transaction");
  const [idValue, setIdValue] = useState("");
  const [providers, setProviders] = useState<any>();
  const getProvider = () => {
    fetchProviders()
      .then((res) => setProviders(res.data))
      .catch((err) => {
        console.error(err);
      });
  };
  const requestLogs = async (idValue?: string) => {
    if (logType === "id" && !idValue) return;

    setLoading(true);
    try {
      let res;

      if (logType === "id") {
        res = await logRequests[idType as "transaction" | "request"](idValue!);
      } else {
        res =
          logType === "incoming"
            ? await logRequests.incoming()
            : await logRequests.outgoing();
      }
      const data = res.data;
      if (logType === "id") {
        setLogs(data.events);
      } else {
        setLogs(data.items);
      }

      setTotalPage(data.total_pages);
    } catch (err) {
      console.log(err);
      setError("Ошибка при загрузке логов");
    } finally {
      setLoading(false);
    }
  };
  const handleTypeChange = (_: any, value: string) => {
    if (!value) return;
    setIdType(value);
    setIdValue("");
  };
  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#635BFF",
      },
      "&:hover fieldset": {
        borderColor: "#635BFF",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#635BFF",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#635BFF",
    },
  };

  const theme = useTheme();
  useEffect(() => {
    if (logType !== "id") {
      requestLogs();
    }
  }, [page, logType]);
  useEffect(() => {
    getProvider();
  }, []);
  return (
    <Box
      sx={{
        width: "96%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        // Без этого колонка не сжимается и нижняя часть, включая пагинацию,
        // уходит за границу контейнера Layout.
        minHeight: 0,
        overflow: "hidden",
        margin: "0 auto",
        py: { xs: 2, sm: 3 },
      }}
    >
      <PageHeader
        title="Логи запросов"
        description="Входящие и исходящие события интеграций"
        icon={ViewListRounded}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<FilterAltIcon />}
              onClick={() => setOpenDrawer(!openDrawer)}
            >
              Фильтры
            </Button>
            <ToggleButtonGroup
              value={logType}
              exclusive
              onChange={handleLogTypeChange}
              color="primary"
              sx={{
                flex: { xs: "1 1 100%", md: "initial" },
                "& .MuiToggleButton-root": {
                  fontSize: isMobile600 ? "12px" : "14px",
                  flex: { xs: 1, md: "initial" },
                },
              }}
            >
              <ToggleButton value="id">ID</ToggleButton>
              <ToggleButton value="incoming">Входящие</ToggleButton>
              <ToggleButton value="outgoing">Исходящие</ToggleButton>
            </ToggleButtonGroup>
          </>
        }
      />
      {logType === "id" ? (
        <Box mt="15px">
          {/* Переключатели сверху */}
          <ToggleButtonGroup
            value={idType}
            exclusive
            onChange={handleTypeChange}
            fullWidth
            sx={{
              mb: 1.5,
              backgroundColor: theme.palette.background.paper,
              borderRadius: "12px",
              "& .MuiToggleButton-root": {
                flex: 1,
                textTransform: "none",
                border: "none",
                borderRadius: "8px",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                transition: "all .2s ease",
              },
              "& .MuiToggleButton-root.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            }}
          >
            <ToggleButton value="transaction">Transaction ID</ToggleButton>
            <ToggleButton value="request">Request ID</ToggleButton>
          </ToggleButtonGroup>

          {/* Инпут + кнопка */}
          <Box
            display="flex"
            gap={2}
            alignItems="center"
            flexDirection={isMobile600 ? "column" : "row"}
          >
            <TextField
              label={idType === "transaction" ? "Transaction ID" : "Request ID"}
              value={idValue}
              onChange={(e) => setIdValue(e.target.value)}
              sx={{
                width: "100%",
                flex: 1,
                ...inputStyles,
              }}
              InputProps={{
                endAdornment: idValue && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setIdValue("")}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              sx={{
                height: "56px",
                minWidth: 140,
                width: isMobile600 ? "100%" : "auto",
              }}
              disabled={!idValue}
              onClick={() => {
                requestLogs(idValue);
              }}
            >
              Применить
            </Button>
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          // Остаток высоты вместо доли экрана: прежние dvh складывались с
          // шапкой и фильтрами и выдавливали пагинацию за нижний край.
          flex: 1,
          minHeight: 0,
          marginTop: "25px",
          borderRadius: "15px",
          overflowY: isMobile600 ? "auto" : "hidden",
          overflowX: "hidden",
        }}
      >
        {loading ? (
          <Loading compact variant="table" />
        ) : error ? (
          <Error error={error} />
        ) : isMobile600 && logs.length > 0 ? (
          <LogsMobileComponent logs={logs} />
        ) : logs.length === 0 ? (
          <EmptyState
            icon={DataObjectRounded}
            title="Логов пока нет"
            description="Для выбранного направления и фильтров события не найдены."
            compact
          />
        ) : (
          <LogsTable rows={logs} logType={logType} />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: isMobile600 ? "center" : "start",
          mt: 2,
        }}
      >
        <Pagination
          count={totalPage}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          variant="outlined"
          shape="rounded"
          size="large"
          siblingCount={1}
          boundaryCount={isMobile600 ? 0 : 1}
          renderItem={(item) => {
            if (
              item.type === "start-ellipsis" ||
              item.type === "end-ellipsis"
            ) {
              return null;
            }
            return <PaginationItem {...item} />;
          }}
          sx={{
            "& .MuiPaginationItem-root": {
              
              fontSize: "16px",
            },
          }}
        />
      </Box>
      <LogsFilters
        filters={filters}
        setFilters={setFilters}
        setOpenDrawer={setOpenDrawer}
        openDrawer={openDrawer}
        requestLogs={requestLogs}
        providers={providers}
        idValue={idValue}
        logType={logType}
      />
    </Box>
  );
};
export default LogsPage;
